import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { connect } from "react-redux";
import { RootState } from "../../models/RootState";
import { UserDetails } from "../../models/UserDetails";
import { getDynamicTextColors } from "../../store/authActions";
import { useTranslation } from "../../hooks/useTranslation";
import BONUS_API from "../../services/bonus";
import { toast } from "react-toastify";

export interface RaffleListItem {
  raffleId?: string;
  instanceId?: string;
  thumbnailImage: string;
  title: string;
  subTitle?: string;
  bannerImage: string;
  bannerTitle?: string;
  bannerDescription?: string;
  descriptionHtml?: string;
  ticketsCount?: number;
  metricValue?: number;
  totalWagered?: number;
  startTime?: number;
  endTime?: number;
  status?: string;
}

interface RaffleInstanceWinner {
  userId: string;
  username: string;
  coupon: string;
  prize: string;
  /** Unit or currency label from API (e.g. points); shown with prize in the Winners table. */
  currency?: string;
}

interface RaffleWinner {
  instanceId: string;
  instanceName: string;
  winners: RaffleInstanceWinner[];
}

type RaffleWinnersApiEnvelope =
  | RaffleWinner[]
  | { winners?: RaffleWinner[]; data?: RaffleWinner[] | { winners?: RaffleWinner[] } };

interface RaffleProgress {
  raffleId: string;
  instanceId: string;
  title: string;
  subTitle: string;
  description: string;
  bannerUrl?: string;
  bannerTitle?: string;
  bannerDescription?: string;
  ticketsCount: number;
  metricValue: number;
  totalWagered: number;
  startTime: number;
  endTime: number;
  status?: string;
}

type RaffleProgressRaw = RaffleProgress & {
  banner_title?: string;
  banner_description?: string;
};

interface RaffleProgressResponse {
  raffles: RaffleProgress[];
}

interface RaffleTicket {
  number: number;
}

interface RaffleTicketsResponse {
  tickets: RaffleTicket[];
  count: number;
  next_page_token: string;
}

type RaffleTicketsApiEnvelope =
  | RaffleTicketsResponse
  | { data?: RaffleTicketsResponse; result?: RaffleTicketsResponse };

const RAFFLE_FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1518895949257-7621c3c886d2?w=1200&q=80";

interface RaffleInfoProps {
  userDetails: UserDetails;
  isTabView?: boolean;
  /** From `/promotions` navigation (e.g. Telegram startapp); opens detail after progress loads. */
  deeplinkRaffleId?: string | number | null;
}

const RaffleInfo: React.FC<RaffleInfoProps> = ({ userDetails, isTabView = false, deeplinkRaffleId }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const raffle = location.state?.raffle as RaffleListItem | undefined;
  const [rafflesLoading, setRafflesLoading] = React.useState<boolean>(false);
  const [rafflesData, setRafflesData] = React.useState<RaffleListItem[] | null>(null);
  const [detailRaffle, setDetailRaffle] = React.useState<RaffleListItem | null>(raffle || null);
  const [raffleWinners, setRaffleWinners] = React.useState<RaffleWinner[]>([]);
  const [selectedInstanceId, setSelectedInstanceId] = React.useState<string>("");
  const [couponCode, setCouponCode] = React.useState<string>("");
  const [claimLoading, setClaimLoading] = React.useState<boolean>(false);
  const [ticketsLoading, setTicketsLoading] = React.useState<boolean>(false);
  const [ticketCount, setTicketCount] = React.useState<number>(0);
  const [ticketNumbers, setTicketNumbers] = React.useState<number[]>([]);
  const [raffleProgressFilter, setRaffleProgressFilter] = React.useState<"ACTIVE" | "COMPLETED">("ACTIVE");

  const { pageTextColorClass, inputBoxTextColorClass, tabTextColorClass, activeTabTextColorClass } =
    getDynamicTextColors(userDetails);

  const formatEpochToDisplay = (epoch?: number) => {
    if (!epoch) return "-";
    const millis = epoch > 1_000_000_000_000 ? epoch : epoch * 1000;
    return new Date(millis).toLocaleString();
  };

  const progressStatusFilter = (status?: string): "ACTIVE" | "COMPLETED" => {
    const s = (status || "").trim().toUpperCase();
    if (s === "COMPLETED" || s.includes("COMPLET")) return "COMPLETED";
    return "ACTIVE";
  };

  const mapProgressToListItem = (raffle: RaffleProgress): RaffleListItem => {
    const raw = raffle as RaffleProgressRaw;
    const banner =
      raffle.bannerUrl && raffle.bannerUrl.trim() !== "" ? raffle.bannerUrl : RAFFLE_FALLBACK_IMAGE;
    const bannerTitle =
      [raw.bannerTitle, raw.banner_title].find((s) => typeof s === "string" && s.trim() !== "")?.trim() ||
      undefined;
    const bannerDescription =
      [raw.bannerDescription, raw.banner_description].find((s) => typeof s === "string" && s.trim() !== "")?.trim() ||
      undefined;
    return {
      raffleId: raffle.raffleId,
      instanceId: raffle.instanceId,
      title: raffle.title,
      subTitle: raffle.subTitle,
      bannerTitle,
      bannerDescription,
      descriptionHtml: raffle.description ? `<p>${raffle.description}</p>` : "",
      ticketsCount: raffle.ticketsCount,
      metricValue: raffle.metricValue,
      totalWagered: raffle.totalWagered,
      startTime: raffle.startTime,
      endTime: raffle.endTime,
      status: raffle.status,
      thumbnailImage: banner,
      bannerImage: banner,
    };
  };

  const parseWinnersList = (data: RaffleWinnersApiEnvelope | undefined): RaffleWinner[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    const envelope = data as { winners?: RaffleWinner[]; data?: RaffleWinner[] | { winners?: RaffleWinner[] } };
    if (Array.isArray(envelope.winners)) return envelope.winners;
    if (Array.isArray(envelope.data)) return envelope.data;
    if (envelope.data && !Array.isArray(envelope.data)) return envelope.data.winners || [];
    return [];
  };

  const formatPrizeDisplay = (winner: RaffleInstanceWinner) => {
    const w = winner as RaffleInstanceWinner & { prizeCurrency?: string; prize_currency?: string };
    const prize = w.prize != null ? String(w.prize).trim() : "";
    const currencyRaw = w.currency ?? w.prizeCurrency ?? w.prize_currency;
    const currency = currencyRaw != null ? String(currencyRaw).trim() : "";
    if (!prize && !currency) return "-";
    if (!currency) return prize || "-";
    if (!prize) return currency;
    const pl = prize.toLowerCase();
    const cl = currency.toLowerCase();
    if (pl === cl || pl.endsWith(cl) || pl.endsWith(` ${cl}`)) return prize;
    return `${prize} ${currency}`;
  };

  const filteredRafflesTabList = useMemo(() => {
    if (!rafflesData?.length) return [];
    return rafflesData.filter((item) => progressStatusFilter(item.status) === raffleProgressFilter);
  }, [rafflesData, raffleProgressFilter]);

  useEffect(() => {
    if (isTabView) {
      return;
    }
    if (!raffle) {
      navigate("/promotions", { replace: true });
    }
  }, [isTabView, raffle, navigate]);

  useEffect(() => {
    if (!isTabView) {
      setDetailRaffle(raffle || null);
    }
  }, [isTabView, raffle]);

  const loadRaffles = async () => {
    setRafflesLoading(true);
    try {
      const response = await BONUS_API.get("/raffles/progress");
      const progress = response?.data as RaffleProgressResponse | undefined;
      setRafflesData((progress?.raffles || []).map(mapProgressToListItem));
    } catch (e) {
      console.log(e);
      setRafflesData([]);
    } finally {
      setRafflesLoading(false);
    }
  };

  useEffect(() => {
    if (isTabView) {
      loadRaffles();
    }
  }, [isTabView]);

  const raffleDeepLinkHandledRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!isTabView || rafflesData === null) {
      return;
    }
    if (deeplinkRaffleId === undefined || deeplinkRaffleId === null || deeplinkRaffleId === "") {
      raffleDeepLinkHandledRef.current = null;
      return;
    }
    const id = String(deeplinkRaffleId).trim();
    if (!id) {
      return;
    }
    if (raffleDeepLinkHandledRef.current === id) {
      return;
    }
    raffleDeepLinkHandledRef.current = id;

    const item = rafflesData.find((r) => String(r.raffleId ?? "") === id);
    if (item) {
      navigate("/promotions", { state: { Pagetab: "raffles" }, replace: true });
      setTimeout(() => {
        navigate("/raffleinfo", { state: { raffle: item } });
      }, 0);
    } else {
      toast.error(t("promotions.raffleNotFound"));
      navigate("/promotions", { state: { Pagetab: "raffles" }, replace: true });
    }
  }, [isTabView, rafflesData, deeplinkRaffleId, navigate, t]);

  const loadRaffleWinners = async (raffleId: string) => {
    try {
      const response = await BONUS_API.get(`/raffles/${raffleId}/winners`);
      const winners = parseWinnersList(response?.data as RaffleWinnersApiEnvelope);
      setRaffleWinners(winners);
      if (winners.length > 0) {
        setSelectedInstanceId((prev) => prev || winners[0].instanceId);
      }
    } catch (e) {
      console.log(e);
      setRaffleWinners([]);
      setSelectedInstanceId("");
    }
  };

  const loadInstanceTickets = async (raffleId: string, instanceId: string) => {
    setTicketsLoading(true);
    try {
      const response = await BONUS_API.get(`/raffles/${raffleId}/tickets`, {
        params: {
          instance_id: instanceId,
          page_size: 25,
          page_token: "",
        },
      });

      const raw = response?.data as RaffleTicketsApiEnvelope | undefined;
      const payload =
        (raw as { data?: RaffleTicketsResponse })?.data ||
        (raw as { result?: RaffleTicketsResponse })?.result ||
        (raw as RaffleTicketsResponse);
      setTicketCount(Number(payload?.count || 0));
      setTicketNumbers((payload?.tickets || []).map((ticket) => ticket.number));
    } catch (e) {
      console.log(e);
      setTicketCount(0);
      setTicketNumbers([]);
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    if (!isTabView && raffle?.raffleId) {
      loadRaffleWinners(raffle.raffleId);
    }
  }, [isTabView, raffle?.raffleId]);

  useEffect(() => {
    if (!isTabView && raffle?.raffleId && selectedInstanceId) {
      loadInstanceTickets(raffle.raffleId, selectedInstanceId);
    }
  }, [isTabView, raffle?.raffleId, selectedInstanceId]);

  const handleRaffleClick = (selectedRaffle: RaffleListItem) => {
    navigate("/raffleinfo", { state: { raffle: selectedRaffle } });
  };

  const handleClaimCoupon = async () => {
    const raffleId = detailRaffle?.raffleId || raffle?.raffleId;
    const code = couponCode.trim();

    if (!raffleId) {
      toast.error("Raffle not found");
      return;
    }
    if (!selectedInstanceId) {
      toast.error("Please select an instance");
      return;
    }
    if (!code) {
      toast.error("Please enter coupon code");
      return;
    }

    setClaimLoading(true);
    try {
      const response = await BONUS_API.post(`/raffles/${raffleId}/coupon/claim`, {
        instanceId: selectedInstanceId,
        code,
      });
      toast.success(response?.data?.message || "Coupon claimed successfully");
      setCouponCode("");
      await loadRaffleWinners(raffleId);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to claim coupon");
    } finally {
      setClaimLoading(false);
    }
  };

  const winnerInstancesToShow: RaffleWinner[] =
    raffleWinners.length > 0
      ? raffleWinners
      : [{ instanceId: "default", instanceName: "Current Instance", winners: [] }];

  const selectedInstance =
    winnerInstancesToShow.find((instance) => instance.instanceId === selectedInstanceId) ||
    winnerInstancesToShow[0];

  return (
    <>
      {isTabView ? (
        <>
          {rafflesLoading ? (
            <div className="flex justify-center items-center py-8">
              <div
                className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
                  pageTextColorClass === "dark-text" ? "border-black" : "border-white"
                }`}
              ></div>
            </div>
          ) : rafflesData && rafflesData.length > 0 ? (
            <div className="space-y-5 pt-4">
              <div className="bonus-tabs -mx-1">
                <div className="bonus-nav">
                  <div
                    className="navi navi-pills flex flex-nowrap w-full overflow-x-auto no-scrollbar"
                    role="tablist"
                    aria-label={t("promotions.raffles")}
                  >
                    <div className="navi-item flex-1 min-w-0">
                      <a
                        role="tab"
                        aria-selected={raffleProgressFilter === "ACTIVE"}
                        tabIndex={raffleProgressFilter === "ACTIVE" ? 0 : -1}
                        className={`navi-link text-center whitespace-nowrap px-2 py-2 text-xs sm:text-sm cursor-pointer ${
                          raffleProgressFilter === "ACTIVE"
                            ? `active ${activeTabTextColorClass}`
                            : tabTextColorClass
                        }`}
                        onClick={() => setRaffleProgressFilter("ACTIVE")}
                      >
                        {t("promotions.rafflesActive")}
                      </a>
                    </div>
                    <div className="navi-item flex-1 min-w-0">
                      <a
                        role="tab"
                        aria-selected={raffleProgressFilter === "COMPLETED"}
                        tabIndex={raffleProgressFilter === "COMPLETED" ? 0 : -1}
                        className={`navi-link text-center whitespace-nowrap px-2 py-2 text-xs sm:text-sm cursor-pointer ${
                          raffleProgressFilter === "COMPLETED"
                            ? `active ${activeTabTextColorClass}`
                            : tabTextColorClass
                        }`}
                        onClick={() => setRaffleProgressFilter("COMPLETED")}
                      >
                        {t("promotions.rafflesCompleted")}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              {filteredRafflesTabList.length === 0 ? (
                <div className="text-center py-8 rounded-2xl border border-border-color/40 bg-inputbox-color/40">
                  <p
                    className={`text-base ${
                      pageTextColorClass === "dark-text" ? "text-gray-700" : "text-gray-300"
                    }`}
                  >
                    {raffleProgressFilter === "COMPLETED"
                      ? t("promotions.noCompletedRaffles")
                      : t("promotions.noActiveRaffles")}
                  </p>
                </div>
              ) : null}
              {filteredRafflesTabList.map((item, index) => (
                <div
                  key={item.raffleId ?? index}
                  className="bg-inputbox-color rounded-2xl overflow-hidden cursor-pointer border border-border-color/50 shadow-[0_10px_28px_rgba(0,0,0,0.2)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.28)] transition-all duration-200"
                  onClick={() => handleRaffleClick(item)}
                >
                  {item.thumbnailImage && (
                    <div className="w-full flex justify-center bg-inputbox-color">
                      <img
                        src={item.thumbnailImage}
                        alt={item.title || t("promotions.raffleBanner")}
                        className="w-full h-auto max-h-[280px] md:max-h-[360px] object-contain block"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = "none";
                        }}
                      />
                    </div>
                  )}

                  <div className="p-4 md:p-5">
                    {item.title && (
                      <h3
                        className={`font-semibold text-lg md:text-xl leading-tight ${
                          inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"
                        }`}
                      >
                        {item.title}
                      </h3>
                    )}
                    {item.descriptionHtml && (
                      <p
                        className={`mt-3 text-sm leading-relaxed ${
                          pageTextColorClass === "dark-text" ? "text-gray-700" : "text-gray-300"
                        }`}
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                        dangerouslySetInnerHTML={{ __html: item.descriptionHtml }}
                      />
                    )}

                    <div className="mt-4 rounded-lg bg-border-color/10 py-2 pl-1 pr-3">
                      <div className="flex items-center justify-between gap-8 md:gap-14">
                        <div className="min-w-0">
                          <div className={`text-[10px] uppercase tracking-wide ${pageTextColorClass === "dark-text" ? "text-gray-600" : "text-gray-400"}`}>
                            Start
                          </div>
                          <div className={`text-xs font-semibold truncate ${inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"}`}>
                            {formatEpochToDisplay(item.startTime)}
                          </div>
                        </div>
                        <div className="min-w-0 text-right">
                          <div className={`text-[10px] uppercase tracking-wide ${pageTextColorClass === "dark-text" ? "text-gray-600" : "text-gray-400"}`}>
                            End
                          </div>
                          <div className={`text-xs font-semibold truncate ${inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"}`}>
                            {formatEpochToDisplay(item.endTime)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p
                className={`text-lg ${
                  pageTextColorClass === "dark-text" ? "text-gray-700" : "text-gray-400"
                }`}
              >
                {t("promotions.noRafflesAvailable")}
              </p>
              <p
                className={`text-sm mt-2 ${
                  pageTextColorClass === "dark-text" ? "text-gray-600" : "text-gray-500"
                }`}
              >
                {t("promotions.checkBackLaterRaffles")}
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col px-4 md:px-6 mt-4 pb-[7rem] py-[4rem] max-w-5xl mx-auto w-full">
          <div className="flex items-center justify-between">
            <div
              className="bg-inputbox-color/90 border border-border-color/50 flex items-center gap-1 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-inputbox-color transition-colors"
              onClick={() => navigate("/promotions", { state: { Pagetab: "raffles" } })}
            >
              <ArrowBackIcon
                className="text-border-color aspect-square w-[18px]"
                style={{ width: "20px", height: "20px" }}
              />
              <span className={`text-sm font-medium ${inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"}`}>
                {t("promotions.goBack")}
              </span>
            </div>
            {selectedInstance?.instanceName && (
              <div className={`text-xs md:text-sm px-3 py-1 rounded-full border border-border-color/50 bg-inputbox-color/60 ${pageTextColorClass === "dark-text" ? "text-gray-700" : "text-gray-200"}`}>
                {selectedInstance.instanceName}
              </div>
            )}
          </div>
          {detailRaffle?.title && (
            <h2
              className={`text-2xl md:text-3xl font-semibold px-1 text-left mt-5 mb-2 tracking-tight ${
                inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"
              }`}
            >
              {detailRaffle.title}
            </h2>
          )}
          {(detailRaffle?.bannerImage || detailRaffle?.bannerDescription) && (
            <div className="mt-2 mb-4 space-y-3">
              {detailRaffle.bannerImage && (
                <div className="relative rounded-2xl overflow-hidden bg-inputbox-color border border-border-color/50 shadow-[0_14px_34px_rgba(0,0,0,0.28)] flex justify-center">
                  <img
                    src={detailRaffle.bannerImage}
                    alt={detailRaffle.title || t("promotions.raffleBanner")}
                    className="w-full h-auto max-h-[320px] md:max-h-[480px] object-contain block"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </div>
              )}
              {detailRaffle.bannerDescription && (
                <div
                  className={`px-1 text-left text-sm md:text-base leading-relaxed ${
                    pageTextColorClass === "dark-text" ? "text-gray-700" : "text-gray-300"
                  } [&_a]:underline`}
                  dangerouslySetInnerHTML={{ __html: detailRaffle.bannerDescription }}
                />
              )}
            </div>
          )}
          {winnerInstancesToShow.length > 0 && (
            <div className="px-4 py-4 md:px-5 md:py-5 mt-4 rounded-2xl border border-border-color/60 bg-inputbox-color/70 backdrop-blur-md shadow-[0_14px_36px_rgba(0,0,0,0.24)]">
              <label
                htmlFor="raffle-instance-select"
                className={`block text-xs uppercase tracking-[0.08em] font-semibold mb-2 ${
                  inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"
                }`}
              >
                Instance
              </label>
              <select
                id="raffle-instance-select"
                value={selectedInstanceId || winnerInstancesToShow[0]?.instanceId || ""}
                onChange={(e) => setSelectedInstanceId(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-border-color bg-inputbox-color/90 text-sm md:text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-border-color/60"
              >
                {winnerInstancesToShow.map((instance) => (
                  <option
                    key={instance.instanceId}
                    value={instance.instanceId}
                    style={{ color: "#111827", backgroundColor: "#FFFFFF" }}
                  >
                    {instance.instanceName || "Current Instance"}
                  </option>
                ))}
              </select>
              <p
                className={`mt-2 text-xs md:text-sm ${
                  pageTextColorClass === "dark-text" ? "text-gray-700" : "text-gray-300"
                }`}
              >
                Choose an instance to view its specific winners.
              </p>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder="Enter coupon code"
                  className="flex-1 px-3 py-2.5 rounded-xl border border-border-color bg-inputbox-color/90 text-sm md:text-base text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-border-color/60"
                />
                <button
                  type="button"
                  onClick={handleClaimCoupon}
                  disabled={claimLoading || !couponCode.trim() || !selectedInstanceId}
                  className={`px-4 py-2.5 rounded-xl border border-border-color bg-border-color text-sm md:text-base font-semibold tracking-wide transition-all duration-200 hover:brightness-110 hover:shadow-[0_6px_20px_rgba(0,0,0,0.25)] ${
                    inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"
                  } ${claimLoading || !couponCode.trim() || !selectedInstanceId ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {claimLoading ? "Claiming..." : "Claim"}
                </button>
              </div>
              <p
                className={`mt-2 text-xs md:text-sm ${
                  pageTextColorClass === "dark-text" ? "text-gray-700" : "text-gray-300"
                }`}
              >
                Enter coupon code to claim your prize for {selectedInstance?.instanceName || "Current Instance"}
              </p>

              <div className="mt-4 rounded-xl border border-border-color/50 bg-inputbox-color/80 px-3 py-3">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm font-medium ${inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"}`}>
                    Your tickets in this instance
                  </span>
                  <span className={`text-base font-semibold ${inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"}`}>
                    {ticketsLoading ? "..." : ticketCount}
                  </span>
                </div>
                {ticketNumbers.length > 0 && (
                  <p className={`mt-2 text-xs ${pageTextColorClass === "dark-text" ? "text-gray-700" : "text-gray-300"}`}>
                    Ticket numbers: {ticketNumbers.join(", ")}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="px-2 mt-6">
            <h3
              className={`text-xl font-semibold mb-3 flex items-center gap-2 ${
                inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"
              }`}
            >
              Winners
            </h3>
            <div className="space-y-4">
              {selectedInstance && (
                  <div key={selectedInstance.instanceId}>
                    <div className="rounded-2xl overflow-hidden border border-[#2f66d2]/70 bg-gradient-to-b from-[#10264a]/70 to-[#0b1630]/50 shadow-[0_10px_28px_rgba(47,102,210,0.22)]">
                      <table className="w-full text-white text-sm md:text-base">
                        <thead>
                          <tr className="border-b border-[#2f66d2] bg-[#2f66d2]/15">
                            <th className="text-left py-4 px-4 font-semibold uppercase tracking-wide text-xs">SNO</th>
                            <th className="text-left py-4 px-4 font-semibold uppercase tracking-wide text-xs">Username</th>
                            <th className="text-left py-4 px-4 font-semibold uppercase tracking-wide text-xs">Coupon</th>
                            <th className="text-left py-4 px-4 font-semibold uppercase tracking-wide text-xs">Prize</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedInstance.winners?.length > 0 ? (
                            selectedInstance.winners.map((winner, idx) => (
                              <tr
                                key={`${selectedInstance.instanceId}-${winner.userId}-${winner.coupon}-${idx}`}
                                className="border-b border-[#2f66d2]/45 last:border-b-0 odd:bg-white/[0.03]"
                              >
                                <td className="py-3.5 px-4">{idx + 1}</td>
                                <td className="py-3.5 px-4">{winner.username || "-"}</td>
                                <td className="py-3.5 px-4 font-medium">{winner.coupon || "-"}</td>
                                <td className="py-3.5 px-4">
                                  {formatPrizeDisplay(winner)}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} className="text-center py-14 text-sm md:text-base font-medium text-gray-200">
                                No winners for this instance
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  userDetails: state.userDetails?.userDetails,
});

export default connect(mapStateToProps)(RaffleInfo);
